import React, { useState } from 'react';
import { useTranslations } from '@components/context';
import { notification } from '@utils/utils';
import useScrollIntoView, { useScrollToIndex } from '@hooks/useScrollIntoView';
import {
    AUTOMATION_OPERATOR_AND,
    AUTOMATION_OPERATOR_OR,
    canAddCondition,
    createConditionRow,
    isOptionValueSelected,
    removeConditionFromGroup,
    toggleValueInList,
} from '../conditionFormHelpers';
import SegmentedControl from '@components/core/SegmentedControl';
import Button from '@components/legacy/core/Button';
import CheckboxField from '@components/core/CheckboxField';
import RadioField from '@components/core/RadioField';
import SelectDropdown from '@components/core/SelectDropdown';

const isBinaryChoiceEnum = (enumList) => {
    if (enumList.length !== 2) {
        return false;
    }
    const values = enumList.map(({ value }) => value);
    return (values.includes(0) && values.includes(1))
        || (values.includes(true) && values.includes(false));
};

export const ConditionOperatorSwitch = ({ value, onChange, t, readOnly, options = [], ariaLabel }) => {
    if (readOnly) {
        return (
            <div className="conditions-switch conditions-switch--readonly">
                <span>{options.find((o) => o.id === value)?.label}</span>
            </div>
        );
    }
    return (
        <SegmentedControl
            ariaLabel={ariaLabel}
            className="conditions-switch"
            value={value}
            onChange={onChange}
            options={options}
        />
    );
};

const ConditionGroup = ({
    event,
    group,
    conditionOptions,
    selectableConditionOptions,
    operatorOptions,
    onChange,
    onRemoveGroup,
    canRemoveGroup = true,
    readOnly = false,
}) => {
    const { t } = useTranslations();
    const conditions = Array.isArray(group?.conditions) ? group.conditions : [];
    // A brand-new (empty) group immediately prompts for its first criterion.
    const [showConditionPicker, setShowConditionPicker] = useState(conditions.length === 0);
    const [collapsed, setCollapsed] = useState({});
    const addConditionControlRef = useScrollIntoView(showConditionPicker);
    const { getRef: getConditionCardRef, requestScroll: requestConditionCardScroll } = useScrollToIndex(conditions);

    const getConditionById = (id) => conditionOptions.find((c) => c.id === id);
    const setConditions = (next) => onChange({ ...group, conditions: next });

    const addCondition = (conditionId) => {
        const conditionOption = conditionOptions.find((c) => c.id === conditionId);
        if (!conditionOption) {
            return;
        }
        if (!canAddCondition(group, conditionOption)) {
            notification(t('Condition already added'), { type: 'warning' });
            return;
        }
        requestConditionCardScroll(conditions.length);
        setConditions([
            ...conditions,
            createConditionRow({ filter: conditionOption.id, value: conditionOption.default_value }),
        ]);
        setShowConditionPicker(false);
    };

    const removeRow = (index) => {
        const nextGroup = removeConditionFromGroup(group, index);
        if (nextGroup === null) {
            onRemoveGroup();
            return;
        }
        onChange(nextGroup);
    };

    const updateRow = (index, patch) => {
        setConditions(conditions.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    };
    const onCheckboxChange = (index, row, optionValue, checked) => {
        updateRow(index, { value: toggleValueInList(row.value, optionValue, checked) });
    };
    const onRadioChange = (index, optionValue) => {
        updateRow(index, { value: [optionValue] });
    };

    const groupModeLabel = group.op === AUTOMATION_OPERATOR_OR ? t('ANY OF') : t('ALL OF');

    return (
        <div className="auto-sched-condition-group">
            <div className="auto-sched-condition-group-head">
                <span className="auto-sched-condition-group-label">
                    {t('GROUP')}
                    {conditions.length >= 2 ? (
                        <span className="auto-sched-condition-group-mode"> · {groupModeLabel}</span>
                    ) : null}
                </span>
                {!readOnly && canRemoveGroup ? (
                    <Button
                        type="link"
                        onlyIcon
                        icon="icon-svg-x-close"
                        className="auto-sched-condition-group-close"
                        aria-label={t('Remove group')}
                        onClick={onRemoveGroup}
                    />
                ) : null}
            </div>

            {conditions.map((row, index) => {
                const conditionOption = getConditionById(row.filter);
                if (!conditionOption) {
                    return null;
                }
                const { enum: enumList, text, apply_default_value: applyDefaultValue } = conditionOption;
                const useRadio = isBinaryChoiceEnum(enumList);
                const showCard = applyDefaultValue || enumList.length > 0;
                const isCollapsed = Boolean(collapsed[row.rowKey]);
                const hasValues = !applyDefaultValue && enumList.length > 0;
                const isMultiSelect = !useRadio && enumList.length > 1;

                return (
                    <React.Fragment key={row.rowKey}>
                        {index > 0 ? (
                            <ConditionOperatorSwitch
                                value={group.op}
                                onChange={(next) => onChange({ ...group, op: next })}
                                ariaLabel={t('Combine conditions')}
                                t={t}
                                readOnly={readOnly}
                                options={operatorOptions}
                            />
                        ) : null}
                        {showCard ? (
                            <div className="auto-sched-condition-card" ref={getConditionCardRef(index)}>
                                <div className="auto-sched-condition-card-head">
                                    {applyDefaultValue ? (
                                        <span
                                            className="auto-sched-condition-card-title"
                                            dangerouslySetInnerHTML={{ __html: text }}
                                        />
                                    ) : (
                                        <span className="auto-sched-condition-card-title">{text}</span>
                                    )}
                                    <span className="auto-sched-condition-card-actions">
                                        {hasValues ? (
                                            <Button
                                                type="link"
                                                onlyIcon
                                                icon="icon-svg-chevron-down"
                                                className={`auto-sched-condition-card-collapse${isCollapsed ? '' : ' expanded'}`}
                                                aria-label={isCollapsed ? t('Expand') : t('Collapse')}
                                                onClick={() => setCollapsed((c) => ({ ...c, [row.rowKey]: !isCollapsed }))}
                                            />
                                        ) : null}
                                        {!readOnly ? (
                                            <Button
                                                type="link"
                                                onlyIcon
                                                icon="icon-svg-x-close"
                                                className="auto-sched-condition-card-close"
                                                aria-label={t('Close')}
                                                onClick={() => removeRow(index)}
                                            />
                                        ) : null}
                                    </span>
                                </div>
                                {hasValues && !isCollapsed ? (
                                    <>
                                        {isMultiSelect ? (
                                            <p className="auto-sched-answers-hint">
                                                {t('{title} — matches any of the selected options.', { title: text })}
                                            </p>
                                        ) : null}
                                        <ul className="auto-sched-answers">
                                            {enumList.map(({ value, label }) => (
                                                <li key={value} className="auto-sched-answer">
                                                    {useRadio ? (
                                                        <RadioField
                                                            label={label}
                                                            radioId={`sched-q-${row.rowKey}-${value}`}
                                                            name={`sched-q-${row.rowKey}`}
                                                            isChecked={isOptionValueSelected(row.value, value)}
                                                            disabled={readOnly}
                                                            onChange={() => onRadioChange(index, value)}
                                                        />
                                                    ) : (
                                                        <CheckboxField
                                                            label={label}
                                                            checkboxId={`sched-q-${row.rowKey}-${value}`}
                                                            isChecked={isOptionValueSelected(row.value, value)}
                                                            disabled={readOnly}
                                                            onChange={(e) => {
                                                                onCheckboxChange(index, row, value, e.target.checked);
                                                            }}
                                                        />
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </>
                                ) : null}
                            </div>
                        ) : null}
                    </React.Fragment>
                );
            })}

            {!readOnly && (showConditionPicker ? (
                <div className="add_condition_control" ref={addConditionControlRef}>
                    <SelectDropdown
                        id={`automation-conditions-editor-condition-${group.groupKey}`}
                        name="condition"
                        className="add-automation-modal__select2 add-automation-modal__select2--conditions automation-field automation-field--condition"
                        options={selectableConditionOptions}
                        value={null}
                        placeholder={t('Select criteria')}
                        allowHtml
                        onChange={(v) => v && addCondition(v)}
                        disabled={!event}
                    />
                </div>
            ) : (
                <Button
                    type="link"
                    noPadding
                    className="auto-sched-criteria-add"
                    icon="icon-svg-plus"
                    disabled={!event}
                    onClick={() => { event && setShowConditionPicker(true); }}
                >
                    {t('Add a criteria')}
                </Button>
            ))}
        </div>
    );
};

export default ConditionGroup;
